import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Register() {
  const [form, setForm] = useState({
    tenant_name: "",
    tenant_slug: "",
    admin_name: "",
    admin_email: "",
    admin_password: "",
  });
  const [busy, setBusy] = useState(false);
  const { registerTenant } = useAuth();
  const navigate = useNavigate();

  function bind(field) {
    return (e) => {
      const val = field === "tenant_slug"
        ? e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "")
        : e.target.value;
      setForm((f) => {
        const next = { ...f, [field]: val };
        if (field === "tenant_name" && !f.tenant_slug) {
          next.tenant_slug = val.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
        }
        return next;
      });
    };
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    const r = await registerTenant(form);
    setBusy(false);
    if (!r.ok) {
      toast.error(r.error || "Registration failed");
      return;
    }
    toast.success("Tenant created — welcome!");
    navigate("/admin", { replace: true });
  }

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col p-8 lg:p-16">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-50 transition-colors w-fit" data-testid="register-back">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>

      <div className="flex-1 flex items-center max-w-lg w-full mx-auto">
        <div className="w-full">
          <div className="mb-10">
            <h1 className="font-display text-4xl tracking-tight font-medium mb-2">Create your tenant</h1>
            <p className="text-slate-400">One workspace per business. Add your partners next.</p>
          </div>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 block">Business name</label>
              <input
                data-testid="register-tenant-name"
                type="text" required value={form.tenant_name} onChange={bind("tenant_name")}
                className="w-full rounded-lg border border-slate-800 bg-slate-900 px-4 py-3 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
                placeholder="Steele Cloud"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 block">Tenant slug</label>
              <input
                data-testid="register-tenant-slug"
                type="text" required pattern="[a-z0-9-]+" value={form.tenant_slug} onChange={bind("tenant_slug")}
                className="w-full rounded-lg border border-slate-800 bg-slate-900 px-4 py-3 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 font-mono"
                placeholder="stech"
              />
              <div className="text-xs text-slate-500 mt-1">Used in webhook URLs. Lowercase letters, numbers, hyphens.</div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 block">Your name</label>
                <input
                  data-testid="register-admin-name"
                  type="text" required value={form.admin_name} onChange={bind("admin_name")}
                  className="w-full rounded-lg border border-slate-800 bg-slate-900 px-4 py-3 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
                  placeholder="Alex Steele"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 block">Email</label>
                <input
                  data-testid="register-admin-email"
                  type="email" required value={form.admin_email} onChange={bind("admin_email")}
                  className="w-full rounded-lg border border-slate-800 bg-slate-900 px-4 py-3 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
                  placeholder="alex@stech.com.au"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 block">Password</label>
              <input
                data-testid="register-admin-password"
                type="password" required minLength={6} value={form.admin_password} onChange={bind("admin_password")}
                className="w-full rounded-lg border border-slate-800 bg-slate-900 px-4 py-3 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
                placeholder="At least 6 characters"
              />
            </div>
            <button
              data-testid="register-submit"
              type="submit" disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 bg-brand-gradient text-slate-950 disabled:opacity-60 px-6 py-3.5 rounded-full font-semibold text-sm transition-all hover:-translate-y-[1px] hover:brightness-110"
            >
              {busy ? "Creating…" : "Create tenant"} {!busy && <ArrowRight className="w-4 h-4" />}
            </button>
          </form>

          <div className="mt-8 text-sm text-slate-400">
            Already have an account?{" "}
            <Link to="/login" data-testid="register-to-login" className="text-cyan-400 hover:text-cyan-300 font-medium">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
